import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Dropzone from "react-dropzone"
import { Helmet, HelmetProvider } from "react-helmet-async"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import api from "../api"
import { ReactComponent as EditIcon } from "../assets/images/icons/ic-image-edit.svg"
import { ReactComponent as StopIcon } from "../assets/images/icons/ic-stop.svg"
import { ReactComponent as UnlockIcon } from "../assets/images/icons/ic-unlock.svg"
import { ReactComponent as InsertImageIllustration } from "../assets/images/illustrations/il-insert-image.svg"
import UserPlaceholder from "../assets/images/placeholders/user-placeholder.png"
import { AccountStatus, AdminRole, ButtonStatus, CompanyAdminRole } from "../common/constants"
import { getDefaultPermissionLevel, isAlwaysEnabledPage, permissionEditorSectionsConfig, permissionPagesConfig } from "../common/permissions"
import Breadcrumb from "../components/Breadcrumb"
import Button from "../components/Button"
import AlertDialog from "../components/dialogs/AlertDialog"
import Dialog from "../components/dialogs/Dialog"
import ImageCropper from "../components/ImageCropper"
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout"
import Skeleton from "../components/Skeleton"
import TextInput from "../components/TextInput"
import { DialogStatus } from "../enums"
import { capitalize } from "../utils"
import styles from "./Admin.module.css"
import Select from "../components/Select"

const Admin = () => {
  const { t } = useTranslation()
  const { adminId, tutorId } = useParams()
  const id = adminId ?? tutorId

  const [admin, setAdmin] = useState(null)
  const [adminLoading, setAdminLoading] = useState(false)
  const [profilePic, setProfilePic] = useState(null)
  const [name, setName] = useState("")
  const [surname, setSurname] = useState("")
  const [hasChanges, setHasChanges] = useState(false)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false)
  const [imgFile, setImgFile] = useState(null)
  const [cropMode, setCropMode] = useState(false)
  const [alert, setAlert] = useState({
    open: false,
    title: "",
    message: "",
    status: DialogStatus.Default,
    actions: []
  })

  const roleOptions = useMemo(() => {
    return [
      { id: 0, name: "Main", value: AdminRole.Main },
      { id: 1, name: "Sales", value: AdminRole.Sales },
      { id: 2, name: "Media", value: AdminRole.Media },
      { id: 3, name: "Nabila", value: AdminRole.Nabila },
      { id: 4, name: "Marketing", value: AdminRole.Marketing },
      { id: 5, name: "Event Manager", value: AdminRole.EventManager },
    ]
  }, [])
  const [role, setRole] = useState(roleOptions[0])
  const [permissions, setPermissions] = useState({ pages: {} })

  const [savingTimeout, setSavingTimeout] = useState(null)
  const [savingStatus, setSavingStatus] = useState(null)
  const [companies, setCompanies] = useState([])
  const [selectedCompany, setSelectedCompany] = useState(null)
  const companyRoleOptions = useMemo(() => {
    return [
      { id: 0, name: "Owner", value: CompanyAdminRole.Owner },
      { id: 1, name: "Admin", value: CompanyAdminRole.Admin },
      { id: 2, name: "Manager", value: CompanyAdminRole.Manager },
      { id: 3, name: "Analyst", value: CompanyAdminRole.Analyst },
      { id: 4, name: "Sales", value: CompanyAdminRole.Sales },
    ]
  }, [])
  const [selectedCompanyRole, setSelectedCompanyRole] = useState(null)

  const dropzoneRef = useRef(null)

  const isDefaultAllowedByRole = useCallback((pageId, roleValue) => {
    const defaultPermissionLevel = getDefaultPermissionLevel(pageId)
    if (Array.isArray(defaultPermissionLevel)) {
      return defaultPermissionLevel.includes(roleValue)
    }

    return roleValue === AdminRole.Main
  }, [])

  const normalizePermissions = useCallback((rawPermissions, roleValue) => {
    const pages = rawPermissions?.pages && typeof rawPermissions.pages === 'object' ? rawPermissions.pages : {}
    return {
      pages: permissionPagesConfig.reduce((acc, page) => {
        const pagePerms = pages[page.id] && typeof pages[page.id] === 'object' ? pages[page.id] : {}
        const defaultAllowed = page.defaultAllowed === false
          ? false
          : isAlwaysEnabledPage(page.id)
            ? true
            : isDefaultAllowedByRole(page.id, roleValue)
        const view = typeof pagePerms.view === 'boolean' ? pagePerms.view : defaultAllowed
        const edit = page.viewOnly
          ? false
          : typeof pagePerms.edit === 'boolean'
            ? pagePerms.edit
            : defaultAllowed
        acc[page.id] = {
          view: isAlwaysEnabledPage(page.id) ? true : view,
          edit: isAlwaysEnabledPage(page.id) ? true : edit,
        }
        return acc
      }, {})
    }
  }, [isDefaultAllowedByRole])

  useEffect(() => {
    const getAdmin = async () => {
      setAdminLoading(true)
      try {
        const admin = await api.get(`/admin/admins/${id}`)
        setAdmin(admin)
        setAdminLoading(false)
        return admin
      } catch (e) {
        setAdminLoading(false)
        console.error(e)
      }
    }

    getAdmin()
  }, [id])

  useEffect(() => {
    const getCompanies = async () => {
      try {
        const response = await api.get(`/admin/companies?page=0&per=500`)
        const mapped = (response?.companies || []).map((c) => ({
          id: c.id,
          value: c.id,
          name: c.name,
        }))
        setCompanies(mapped)
      } catch (e) {
        console.error(e)
      }
    }

    getCompanies()
  }, [])

  useEffect(() => {
    return () => {
      if (savingTimeout) {
        clearTimeout(savingTimeout)
      }
    }
  }, [savingTimeout])


  useEffect(() => {
    if (admin) {
      setName(admin.name)
      setSurname(admin.surname)
      setRole(roleOptions.find(r => r.value === admin.role))
      const companyId = admin?.company_id ?? admin?.company?.id ?? null
      setSelectedCompany(companyId ? { id: companyId, value: companyId, name: admin?.company?.name || String(companyId) } : null)
      setSelectedCompanyRole(admin?.company_role ? (companyRoleOptions.find((option) => option.value === admin.company_role) || null) : null)
      setPermissions(normalizePermissions(admin.permissions, admin.role))
    }
  }, [admin, roleOptions, normalizePermissions, companyRoleOptions])

  useEffect(() => {
    if (admin) {
      setHasChanges(
        (name !== admin.name) ||
        (surname !== admin.surname) ||
        (role.value !== admin.role) ||
        ((selectedCompany?.value ?? null) !== (admin?.company_id ?? admin?.company?.id ?? null)) ||
        ((selectedCompanyRole?.value ?? null) !== (admin?.company_role ?? null)) ||
        (!arePermissionsEqual(permissions, normalizePermissions(admin.permissions, admin.role))) ||
        ((profilePic && profilePic !== admin.profilePic) ?? false)
      )
    }
  }, [admin, name, surname, role, permissions, profilePic, normalizePermissions, selectedCompany, selectedCompanyRole])

  useEffect(() => {
    if (savingStatus === ButtonStatus.Success || savingStatus === ButtonStatus.Error) {
      setSavingTimeout(setTimeout(r => setSavingStatus(null), 3000))
    }
  }, [savingStatus])

  const saveChanges = async () => {
    setSavingStatus(ButtonStatus.Loading)
    try {
      const formData = new FormData()
      formData.append("name", name)
      formData.append("surname", surname)
      formData.append("role", roleOptions[role.id].value)
      formData.append("permissions", JSON.stringify(permissions))
      formData.append("company_id", String(selectedCompany?.value ?? -1))
      if (selectedCompany?.value && selectedCompanyRole?.value) {
        formData.append("company_role", selectedCompanyRole.value)
      }

      if (profilePic) {
        let blob = await fetch(profilePic).then(r => r.blob());
        formData.append("picture", blob, `${admin.id}-picture.jpg`);
      }

      try {
        const admin = await api.put(`/admin/admins/${id}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        setAdmin(admin)
      } catch (e) {
        throw new Error("Non è stato possibile salvare le modifiche.")
      }

      // setDialogOpen(true)
      setAlert({ open: true, title: t('profile.saved'), message: t('profile.changesSaved'), status: DialogStatus.Success })
      setSavingStatus(ButtonStatus.Success)
    } catch (e) {
      console.error(e)
      setSavingStatus(ButtonStatus.Error)
    }
  }

  const resetAlert = useCallback(() => {
    setAlert({
      open: false,
      title: "",
      message: "",
      status: DialogStatus.Default,
      actions: []
    })
  }, [])

  const changeAccountStatus = useCallback(async (status) => {
    setAlert(a => { return { ...a, status: DialogStatus.Loading } })

    try {
      const admin = await api.patch(`/admin/admins/${id}`, {
        status
      })
      setAdmin(admin)
      setAlert(a => { return { ...a, status: DialogStatus.Success } })
    } catch (e) {
      console.error(e)
      setAlert(a => { return { ...a, status: DialogStatus.Error } })
    }
  }, [id])

  function arePermissionsEqual(a, b) {
    const aPages = a?.pages ?? {}
    const bPages = b?.pages ?? {}

    return permissionPagesConfig.every((page) => {
      const key = page.id
      const aPerm = aPages[key] ?? {}
      const bPerm = bPages[key] ?? {}
      return Boolean(aPerm.view) === Boolean(bPerm.view) && Boolean(aPerm.edit) === Boolean(bPerm.edit)
    })
  }

  const getPermissionLabel = useCallback((page) => {
    const pageId = typeof page === 'string' ? page : page?.id
    if (page?.label) {
      return page.label
    }

    const mappedLabel = t(`sidenav.${pageId}`)
    if (mappedLabel && mappedLabel !== `sidenav.${pageId}`) {
      return mappedLabel
    }

    const fallback = pageId
      .replace(/\//g, ' / ')
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase())
    return fallback
  }, [t])

  const setPagePermission = useCallback((pageId, action, value) => {
    setPermissions((prev) => {
      if (isAlwaysEnabledPage(pageId)) {
        return prev
      }

      const currentPage = prev?.pages?.[pageId] ?? { view: false, edit: false }
      const nextPage = { ...currentPage, [action]: value }

      if (action === 'edit' && value) {
        nextPage.view = true
      }

      if (action === 'view' && value === false) {
        nextPage.edit = false
      }

      return {
        pages: {
          ...prev.pages,
          [pageId]: nextPage,
        }
      }
    })
  }, [])

  return (
    <HeaderFooterLayout>
      <HelmetProvider>
        <Helmet>
          <title>{admin ? `${admin.name} ${admin.surname} | Amministratore` : "Amministratore"}</title>
        </Helmet>
      </HelmetProvider>
      <div className={styles.container}>
        <div className={styles.section}>
          <div className={styles.sectionInner}>
            <Breadcrumb items={[{label: 'Utenti', to: '/users'}, {label: 'Admin', to: '/users/admins'}, {label: admin ? `${admin.name} ${admin.surname}` : 'Profilo'}]} />
            <header className={styles.pageHeader}><h1>Profilo admin</h1><p>Gestisci i dati dell’amministratore, l’azienda associata e i permessi di accesso.</p></header>
            <div className={styles.content}>
              <div className={styles.leftColumn}>
                <div className={styles.admin}>
                  <img className={styles.profilePic} src={(profilePic || admin?.picture) ?? UserPlaceholder} alt='' />
                  {
                    adminLoading &&
                    <Skeleton type="rect" width="164px" height="24px" borderRadius={"12px"} />
                  }
                  {
                    !adminLoading &&
                    <div className={styles.bio}>
                      <div className={styles.username}>{`${admin?.name} ${admin?.surname}`}</div>
                      <div className={styles.email}>{admin?.email}</div>
                    </div>

                  }
                  <Button
                    appearance="subtle" accentColor="var(--primary)"
                    onClick={() => {
                      setDialogOpen(true)
                    }}
                  >
                    <EditIcon />
                    Cambia foto
                  </Button>
                  {
                    admin?.status === AccountStatus.Active &&
                    <Button
                      appearance="subtle" accentColor="var(--sf-red)"
                      onClick={() => {
                        setAlert({
                          open: true,
                          title: "Sospensione amministratore",
                          message: "L'amministratore non potrà più accedere alla piattaforma backoffice. Sei sicuro di voler procedere?",
                          status: DialogStatus.Default,
                          actions: [
                            {
                              label: "ANNULLA",
                              onClick: resetAlert,
                              inverse: true
                            },
                            {
                              label: "SI, SOSPENDI",
                              onClick: () => changeAccountStatus(AccountStatus.Suspended),
                            }
                          ]
                        })
                      }}
                    >
                      <StopIcon style={{ transform: "rotate(-45deg)" }} />
                      Sospendi
                    </Button>
                  }

                  {
                    admin?.status === AccountStatus.Suspended &&
                    <Button
                      accentColor={"var(--primary)"}
                      onClick={() => {
                        setAlert({
                          open: true,
                          title: "Attivazione amministratore",
                          message: "L'amministratore potrà accedere alla piattaforma backoffice. Sei sicuro di voler procedere?",
                          status: DialogStatus.Default,
                          actions: [
                            {
                              label: "ANNULLA",
                              onClick: resetAlert,
                              inverse: true
                            },
                            {
                              label: "SI, ATTIVA",
                              onClick: () => changeAccountStatus(AccountStatus.Active),
                            }
                          ]
                        })
                      }}
                    >
                      <UnlockIcon />
                      Attiva
                    </Button>
                  }

                </div>
              </div>
              <div className={styles.rightColumn}>
                <div className={styles.card}>
                  <div className={styles.cardTitle}>{t("profile.editTitle")}</div>
                  <div className={styles.inputs}>
                    <div className={styles.row}>
                      <TextInput appearance="modern" value={name} type="text" label={capitalize(t('profile.name'))} placeholder={capitalize(t('profile.name'))} onKeyUp={v => setName(v)}></TextInput>
                      <TextInput appearance="modern" value={surname} type="text" label={capitalize(t('profile.surname'))} placeholder={capitalize(t('profile.surname'))} onKeyUp={v => setSurname(v)}></TextInput>
                    </div>
                    <div className={styles.field}><span className={styles.fieldLabel}>Ruolo</span><Select appearance="modern" portal selected={role} options={roleOptions} onSelect={(r) => setRole(r)} /></div>
                    <div className={styles.field}><span className={styles.fieldLabel}>Azienda</span><Select appearance="modern" portal
                      selected={selectedCompany}
                      options={companies}
                      nullable
                      nullLabel="Nessuna azienda"
                      placeholder="Azienda"
                      onSelect={(c) => {
                        const nextCompany = c?.id === -1 ? null : c
                        setSelectedCompany(nextCompany)
                        if (!nextCompany) {
                          setSelectedCompanyRole(null)
                        }
                      }}
                    /></div>
                    {selectedCompany &&
                      <div className={styles.field}><span className={styles.fieldLabel}>Ruolo aziendale</span><Select appearance="modern" portal
                        selected={selectedCompanyRole}
                        options={companyRoleOptions}
                        placeholder="Ruolo aziendale *"
                        onSelect={(r) => setSelectedCompanyRole(r)}
                      /></div>
                    }
                    <div className={styles.permissionsButtonRow}>
                      <Button
                        appearance="subtle"
                        onClick={() => setPermissionsDialogOpen(true)}
                        style={{ borderRadius: 8, padding: ".65rem 1rem" }}
                      >
                        Personalizza permessi
                      </Button>
                    </div>
                  </div>
                  <div style={{ alignSelf: "flex-end" }}>
                    <Button
                      status={savingStatus}
                      accentColor={"var(--primary)"}
                      style={{ padding: ".65rem 1rem", borderRadius: 8, fontSize: ".75rem" }}
                      disabled={savingStatus === ButtonStatus.Loading || hasChanges === false || (selectedCompany !== null && !selectedCompanyRole)}
                      onClick={saveChanges}
                    >
                      Salva modifiche
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <Dialog appearance="modern"
              open={dialogOpen}
              style={{
                width: "100%",
                maxWidth: "512px"
              }}
              onClose={() => {
                setDialogOpen(false)
                setCropMode(false)
              }}
              title={"Immagine del profilo"}>
              <div className={styles.dialog}>
                {!cropMode &&
                  <Dropzone
                    onDrop={(files) => {
                      if (files[0]) {
                        setImgFile(files[0])
                        setCropMode(true)
                      }
                    }}
                    accept={{
                      'image/jpeg': [],
                      'image/png': [],
                      'image/heic': []
                    }}
                  >
                    {({ getRootProps, getInputProps }) => (
                      <section style={{ display: 'flex', width: '100%' }}>
                        <div {...getRootProps()} style={{ display: 'flex', width: '100%' }}>
                          <input {...getInputProps()} ref={dropzoneRef} />
                          <div className={styles.editPictureDropzone}>
                            <InsertImageIllustration></InsertImageIllustration>
                            <div className={styles.editPictureLabel}>
                              {t('profile.dropImage')}
                            </div>
                            <Button
                              style={{ marginTop: '.5rem', padding: '0.6rem 2rem' }}
                              appearance="subtle" accentColor="var(--primary)"
                              onClick={() => dropzoneRef.current?.click()}
                            >
                              Scegli immagine
                            </Button>
                          </div>
                        </div>
                      </section>
                    )}
                  </Dropzone>
                }
                {cropMode &&
                  <>
                    <ImageCropper
                      src={URL.createObjectURL(imgFile)}
                      onSave={(image) => {
                        setProfilePic(image)
                        setCropMode(false)
                        setDialogOpen(false)
                      }}
                      onCancel={() => { setCropMode(false) }}
                      compression={{ quality: 0.7, maxWidth: 300, maxHeight: 300 }}
                    >
                    </ImageCropper>
                  </>
                }
              </div>
            </Dialog>

            <Dialog appearance="modern"
              open={permissionsDialogOpen}
              style={{
                width: "100%",
                maxWidth: "860px"
              }}
              onClose={() => setPermissionsDialogOpen(false)}
              title={"Permessi amministratore"}
              action={<div className={styles.permissionsFooter}><span>Le modifiche vengono applicate con “Salva modifiche” nel profilo.</span><Button appearance="subtle" onClick={() => setPermissionsDialogOpen(false)}>Chiudi</Button></div>}
            >
              <div className={styles.permissionsDialog}>
                <p className={styles.permissionsIntro}>Scegli quali sezioni può visualizzare o modificare l’amministratore. La modifica include sempre la visualizzazione.</p>
                {permissionEditorSectionsConfig.map((section) => (
                  <div className={styles.permissionsSection} key={section.id}>
                    <div className={styles.permissionsHeaderRow}>
                      <div className={styles.permissionsPageTitle}>
                        {t(`sidenav.${section.id}`) !== `sidenav.${section.id}` ? t(`sidenav.${section.id}`) : 'Generale'}
                      </div>
                      <div className={styles.permissionsActions}>
                        <span>Visualizzazione</span>
                        <span>Modifica</span>
                      </div>
                    </div>

                    <div className={styles.permissionsList}>
                      {section.pages.map((page) => {
                        const pagePermissions = permissions?.pages?.[page.id] ?? { view: false, edit: false }
                        const alwaysEnabled = isAlwaysEnabledPage(page.id)

                        return (
                          <div className={`${styles.permissionsRow} ${page.parentId ? styles.permissionsSubRow : ''}`} key={page.id}>
                            <div className={`${styles.permissionsPageName} ${page.parentId ? styles.permissionsSubPageName : ''}`}>{getPermissionLabel(page)}{alwaysEnabled && <span className={styles.alwaysEnabled}>Sempre attivo</span>}</div>
                            <div className={styles.permissionsActions}>
                              <input type="checkbox" className={styles.permissionCheckbox} aria-label={`Visualizzazione · ${getPermissionLabel(page)}`} checked={Boolean(pagePermissions.view)} disabled={alwaysEnabled} onChange={event => setPagePermission(page.id, 'view', event.target.checked)} />
                              {page.viewOnly ? (
                                <span className={styles.permissionsNotApplicable}>-</span>
                              ) : (
                                <input type="checkbox" className={styles.permissionCheckbox} aria-label={`Modifica · ${getPermissionLabel(page)}`} checked={Boolean(pagePermissions.edit)} disabled={alwaysEnabled} onChange={event => setPagePermission(page.id, 'edit', event.target.checked)} />
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </Dialog>

            <AlertDialog
              open={alert.open}
              title={alert.title}
              text={alert.message}
              onClose={alert.onClose ?? resetAlert}
              actions={alert.actions}
              status={alert.status}
            />
          </div>
        </div>
      </div>
    </HeaderFooterLayout >
  )
}

export default Admin
